import { customDisplayTypesService, dealsService, displayTypesService, locationOrderItemsService, locationOrdersService, quotesService } from '@/lib/services';
import type { Invoice, InvoiceLineItem, InvoiceStatus } from '@/types/invoices';
import type { LocationOrder, LocationOrderItem } from '@/types/location-orders';

export interface EquipmentCandidate {
  display_type_id: string;
  quantity: number;
}

export interface EquipmentSyncResult {
  triggered: boolean;
  locationId: string | null;
  orderId: string | null;
  applied: EquipmentCandidate[];
  skippedOverlaps: EquipmentCandidate[];
  skippedNonEligibleCount: number;
}

export type InvoiceEquipmentResolution = 'keep' | 'new' | 'combine';

export interface InvoiceEquipmentConflict {
  displayTypeId: string;
  displayTypeName: string;
  existingItemId: string;
  currentValue: number;
  invoiceValue: number;
  combinedValue: number;
}

export interface InvoiceEquipmentSyncPlan {
  triggered: boolean;
  locationId: string | null;
  orderId: string | null;
  nonConflicts: EquipmentCandidate[];
  conflicts: InvoiceEquipmentConflict[];
  skippedNonEligibleCount: number;
}

export interface InvoiceEquipmentApplyResult {
  triggered: boolean;
  locationId: string | null;
  orderId: string | null;
  created: EquipmentCandidate[];
  updated: Array<EquipmentCandidate & { previousQuantity: number; resolution: 'new' | 'combine' }>;
  kept: InvoiceEquipmentConflict[];
  skippedNonEligibleCount: number;
}

export function shouldTriggerInvoicePaidSync(
  previousStatus: InvoiceStatus | null | undefined,
  nextStatus: InvoiceStatus | null | undefined
): boolean {
  return previousStatus !== 'paid' && nextStatus === 'paid';
}

export function buildEquipmentCandidatesFromInvoiceLineItems(lineItems: InvoiceLineItem[]): {
  candidates: EquipmentCandidate[];
  skippedNonEligibleCount: number;
} {
  const aggregatedByDisplayType = new Map<string, number>();
  let skippedNonEligibleCount = 0;

  for (const lineItem of lineItems) {
    if (!lineItem.display_type_id || lineItem.quantity <= 0) {
      skippedNonEligibleCount += 1;
      continue;
    }

    const current = aggregatedByDisplayType.get(lineItem.display_type_id) || 0;
    aggregatedByDisplayType.set(lineItem.display_type_id, current + lineItem.quantity);
  }

  const candidates: EquipmentCandidate[] = Array.from(aggregatedByDisplayType.entries()).map(
    ([display_type_id, quantity]) => ({
      display_type_id,
      quantity,
    })
  );

  return { candidates, skippedNonEligibleCount };
}

export async function resolveInvoiceLocationId(invoice: Invoice): Promise<string | null> {
  if (invoice.location_id) return invoice.location_id;

  if (invoice.location_order_id) {
    const locationOrder = await locationOrdersService.getById(invoice.location_order_id);
    if (locationOrder?.location_id) return locationOrder.location_id;
  }

  if (invoice.quote_id) {
    const quote = await quotesService.getById(invoice.quote_id);
    if (quote?.location_id) return quote.location_id;
  }

  if (invoice.deal_id) {
    const deal = await dealsService.getById(invoice.deal_id);
    if (deal?.location_id) return deal.location_id;
  }

  return null;
}

async function ensureLocationOrder(input: {
  locationId: string;
  dealId?: string | null;
}): Promise<LocationOrder> {
  const { locationId, dealId } = input;

  let targetOrder = (await locationOrdersService.getByLocationId(locationId))[0] || null;
  if (!targetOrder) {
    const today = new Date().toISOString().split('T')[0] || new Date().toISOString();
    targetOrder = await locationOrdersService.create({
      location_id: locationId,
      deal_id: dealId || null,
      order_date: today,
      status: 'pending',
      notes: 'Auto-created from paid invoice equipment sync',
    });
  }

  return targetOrder;
}

async function mapDisplayTypeNames(conflictCandidates: EquipmentCandidate[]): Promise<Map<string, string>> {
  const [standardTypes, customTypes] = await Promise.all([
    displayTypesService.getAll(),
    customDisplayTypesService.getAll(),
  ]);
  const types = [...standardTypes, ...customTypes];
    const byId = new Map(types.map((type) => [type.id, type.name]));
    const names = new Map<string, string>();

    for (const candidate of conflictCandidates) {
      names.set(candidate.display_type_id, byId.get(candidate.display_type_id) || candidate.display_type_id);
    }

    return names;
}

function buildEmptyPlan(): InvoiceEquipmentSyncPlan {
  return {
    triggered: false,
    locationId: null,
    orderId: null,
    nonConflicts: [],
    conflicts: [],
    skippedNonEligibleCount: 0,
  };
}

export async function planInvoicePaidEquipmentSync(input: {
  invoice: Invoice;
  previousStatus: InvoiceStatus | null | undefined;
}): Promise<InvoiceEquipmentSyncPlan> {
  const { invoice, previousStatus } = input;
  const triggered = shouldTriggerInvoicePaidSync(previousStatus, invoice.status);

  if (!triggered) {
    return buildEmptyPlan();
  }

  const locationId = await resolveInvoiceLocationId(invoice);
  if (!locationId) {
    return {
      triggered: true,
      locationId: null,
      orderId: null,
      nonConflicts: [],
      conflicts: [],
      skippedNonEligibleCount: invoice.line_items.length,
    };
  }

  const { candidates, skippedNonEligibleCount } = buildEquipmentCandidatesFromInvoiceLineItems(invoice.line_items);
  if (candidates.length === 0) {
    return {
      triggered: true,
      locationId,
      orderId: null,
      nonConflicts: [],
      conflicts: [],
      skippedNonEligibleCount,
    };
  }

  const targetOrder = await ensureLocationOrder({
    locationId,
    dealId: invoice.deal_id,
  });

  const existingItems = await locationOrderItemsService.getByOrderId(targetOrder.id);
  const existingByDisplayType = new Map(existingItems.map((item) => [item.display_type_id, item]));

  const nonConflicts: EquipmentCandidate[] = [];
  const conflictCandidates: EquipmentCandidate[] = [];

  for (const candidate of candidates) {
    if (existingByDisplayType.has(candidate.display_type_id)) {
      conflictCandidates.push(candidate);
    } else {
      nonConflicts.push(candidate);
    }
  }

  const displayTypeNames = await mapDisplayTypeNames(conflictCandidates);

  const conflicts: InvoiceEquipmentConflict[] = conflictCandidates
    .map((candidate) => {
      const existingItem = existingByDisplayType.get(candidate.display_type_id);
      if (!existingItem) return null;

      return {
        displayTypeId: candidate.display_type_id,
        displayTypeName: displayTypeNames.get(candidate.display_type_id) || candidate.display_type_id,
        existingItemId: existingItem.id,
        currentValue: existingItem.quantity,
        invoiceValue: candidate.quantity,
        combinedValue: existingItem.quantity + candidate.quantity,
      };
    })
    .filter((conflict): conflict is InvoiceEquipmentConflict => Boolean(conflict));

  return {
    triggered: true,
    locationId,
    orderId: targetOrder.id,
    nonConflicts,
    conflicts,
    skippedNonEligibleCount,
  };
}

function mapExistingByDisplayType(existingItems: LocationOrderItem[]): Map<string, LocationOrderItem> {
  return new Map(existingItems.map((item) => [item.display_type_id, item]));
}

export async function applyInvoiceEquipmentResolutions(input: {
  plan: InvoiceEquipmentSyncPlan;
  resolutions?: Map<string, InvoiceEquipmentResolution>;
}): Promise<InvoiceEquipmentApplyResult> {
  const { plan, resolutions = new Map<string, InvoiceEquipmentResolution>() } = input;

  if (!plan.triggered) {
    return {
      triggered: false,
      locationId: null,
      orderId: null,
      created: [],
      updated: [],
      kept: [],
      skippedNonEligibleCount: 0,
    };
  }

  if (!plan.locationId || !plan.orderId) {
    return {
      triggered: true,
      locationId: plan.locationId,
      orderId: plan.orderId,
      created: [],
      updated: [],
      kept: [],
      skippedNonEligibleCount: plan.skippedNonEligibleCount,
    };
  }

  const existingItems = await locationOrderItemsService.getByOrderId(plan.orderId);
  const existingByDisplayType = mapExistingByDisplayType(existingItems);

  const conflictIds = new Set(plan.conflicts.map((conflict) => conflict.displayTypeId));
  const resolutionIds = new Set(resolutions.keys());
  if (
    conflictIds.size !== resolutionIds.size ||
    Array.from(conflictIds).some((displayTypeId) => !resolutionIds.has(displayTypeId))
  ) {
    throw new Error('Choose keep, new, or combine for every equipment conflict.');
  }

  const created: EquipmentCandidate[] = [];
  const updated: Array<EquipmentCandidate & { previousQuantity: number; resolution: 'new' | 'combine' }> = [];
  const kept: InvoiceEquipmentConflict[] = [];

  for (const candidate of plan.nonConflicts) {
    await locationOrderItemsService.create({
      order_id: plan.orderId,
      display_type_id: candidate.display_type_id,
      quantity: candidate.quantity,
      unit_price: null,
    });
    created.push(candidate);
  }

  for (const conflict of plan.conflicts) {
    const resolution = resolutions.get(conflict.displayTypeId);
    const existing = existingByDisplayType.get(conflict.displayTypeId);

    if (!existing || !resolution) {
      throw new Error('Equipment changed after review. Refresh and resolve every conflict again.');
    }

    if (resolution === 'keep') {
      kept.push({
        ...conflict,
        currentValue: existing.quantity,
        combinedValue: existing.quantity + conflict.invoiceValue,
      });
      continue;
    }

    const nextQuantity = resolution === 'new' ? conflict.invoiceValue : existing.quantity + conflict.invoiceValue;
    await locationOrderItemsService.update(existing.id, { quantity: nextQuantity });

    updated.push({
      display_type_id: conflict.displayTypeId,
      quantity: nextQuantity,
      previousQuantity: existing.quantity,
      resolution,
    });
  }

  return {
    triggered: true,
    locationId: plan.locationId,
    orderId: plan.orderId,
    created,
    updated,
    kept,
    skippedNonEligibleCount: plan.skippedNonEligibleCount,
  };
}

export async function applyNonConflictEquipmentSync(input: {
  locationId: string;
  dealId?: string | null;
  items: EquipmentCandidate[];
}): Promise<Omit<EquipmentSyncResult, 'triggered' | 'locationId'>> {
  const { locationId, dealId, items } = input;

  const targetOrder = await ensureLocationOrder({
    locationId,
    dealId,
  });

  const existingItems = await locationOrderItemsService.getByOrderId(targetOrder.id);
  const existingByDisplayType = new Map(existingItems.map((item) => [item.display_type_id, item]));

  const applied: EquipmentCandidate[] = [];
  const skippedOverlaps: EquipmentCandidate[] = [];

  for (const item of items) {
    if (existingByDisplayType.has(item.display_type_id)) {
      skippedOverlaps.push(item);
      continue;
    }

    await locationOrderItemsService.create({
      order_id: targetOrder.id,
      display_type_id: item.display_type_id,
      quantity: item.quantity,
      unit_price: null,
    });
    applied.push(item);
  }

  return {
    orderId: targetOrder.id,
    applied,
    skippedOverlaps,
    skippedNonEligibleCount: 0,
  };
}

export async function runInvoicePaidNonConflictEquipmentSync(input: {
  invoice: Invoice;
  previousStatus: InvoiceStatus | null | undefined;
}): Promise<EquipmentSyncResult> {
  const plan = await planInvoicePaidEquipmentSync(input);
  if (!plan.triggered) {
    return {
      triggered: false,
      locationId: null,
      orderId: null,
      applied: [],
      skippedOverlaps: [],
      skippedNonEligibleCount: 0,
    };
  }

  if (!plan.locationId) {
    return {
      triggered: true,
      locationId: null,
      orderId: null,
      applied: [],
      skippedOverlaps: [],
      skippedNonEligibleCount: plan.skippedNonEligibleCount,
    };
  }

  const applyResult = await applyInvoiceEquipmentResolutions({
    plan: {
      ...plan,
      conflicts: [],
    },
  });

  return {
    triggered: true,
    locationId: plan.locationId,
    orderId: applyResult.orderId,
    applied: applyResult.created,
    skippedOverlaps: plan.conflicts.map((conflict) => ({
      display_type_id: conflict.displayTypeId,
      quantity: conflict.invoiceValue,
    })),
    skippedNonEligibleCount: plan.skippedNonEligibleCount,
  };
}
